import type { DueBucket, PresetRole } from '@projoint/shared';

import glossaryMessages from '@/shared/i18n/en/glossary';
import { labelMap, tr } from '@/shared/i18n/translate';

import type { GlossaryKey } from './glossary-terms';

/**
 * The product glossary in the UI (docs/release-1/17-V2-BUILD-PLAN.md §2.4):
 * `term()` and the label maps below, in the current language. The term table
 * itself (EN + RU + retired words) is `GLOSSARY` in `./glossary-terms`.
 */
export type { GlossaryEntry, GlossaryKey } from './glossary-terms';

/** The UI word for a glossary key in the current language: `term('myWork')` → "My Work" / "Моя работа". */
export function term(key: GlossaryKey): string {
  return tr(glossaryMessages, key);
}

/** "Carried over (3)" — the rollover counter. */
export function carriedOverLabel(count: number): string {
  return tr(glossaryMessages, 'carriedOverCount', { count });
}

/**
 * Team role labels (read in the current language on every access).
 * OUTSTAFF → Contractor, VIEWER → Guest (so it doesn't clash with Watcher).
 */
export const ROLE_LABEL: Readonly<Record<PresetRole, string>> = labelMap(glossaryMessages, {
  OWNER: 'owner',
  MANAGER: 'manager',
  LEAD: 'lead',
  MEMBER: 'member',
  OUTSTAFF: 'contractor',
  VIEWER: 'guest',
});

/** Label for any role string; custom roles keep their own name. */
export function roleLabel(role: string): string {
  return (ROLE_LABEL as Readonly<Record<string, string>>)[role] ?? role;
}

/** Group labels for `dueBucket()`. 'later' reads "Upcoming" so it never collides with My Work's Later list. */
export const DUE_BUCKET_LABEL: Readonly<Record<DueBucket, string>> = labelMap(glossaryMessages, {
  overdue: 'overdue',
  today: 'today',
  tomorrow: 'dueTomorrow',
  this_week: 'dueThisWeek',
  later: 'dueLater',
  no_date: 'dueNoDate',
});

/** Mobile bottom-nav labels keyed by the internal `users.mobile_default_tab` ids. */
export const MOBILE_TAB_LABEL: Readonly<
  Record<'dashboard' | 'tasks' | 'chat' | 'workspace', string>
> = labelMap(glossaryMessages, {
  dashboard: 'home',
  tasks: 'myWork',
  chat: 'chat',
  workspace: 'projects',
});

/** Privacy copy (§2.2). Public acts name the team; private ones say so. */
export const PRIVACY_COPY: Readonly<
  Record<'onlyYou' | 'ownersCantSee' | 'markedDoneInPlan' | 'sentForReview', string>
> = labelMap(glossaryMessages, {
  onlyYou: 'privacyOnlyYou',
  ownersCantSee: 'privacyOwnersCantSee',
  markedDoneInPlan: 'privacyMarkedDoneInPlan',
  sentForReview: 'privacySentForReview',
});

/** "Visible to Acme" — caption on public acts such as ▶ timer or a status change. */
export function visibleToLabel(teamName: string): string {
  return tr(glossaryMessages, 'visibleTo', { team: teamName });
}

/** "Create in WEB for the team" — the explicit public-capture toggle. */
export function createForTeamLabel(projectKey: string): string {
  return tr(glossaryMessages, 'createForTeam', { projectKey });
}
